import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertCircle,
  Save,
  X,
  ArrowLeft,
  Upload,
  Undo,
  Redo,
  Download,
  FileEdit,
  FilePlus2,
  History,
  Info,
  MessageSquare,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EditCostBudgetForm, flushPendingCcCostBudgetSaves } from './edit-cost-budget-form';
import { BudgetRevisionPanel } from './budget-revision-panel';
import type {
  BudgetData,
  CostBudget,
  CostCategory,
  BudgetRevision,
  RevisionReasonCategory,
  ResourceBudget,
} from '@/types/budget.types';
import { toast } from 'sonner';
import { fetchDefaultCategoriesAsCostCategories } from '@/lib/data-service';
import type { CostCentre } from '@/components/blocks/cost-centres/cost-centres-table';
import type { HierarchicalCostCentre } from '@/components/blocks/cost-centres/cost-centres-hierarchical-table';

interface EditCostBudgetPageProps {
  budgetData: BudgetData;
  projectStartDate?: string;
  projectEndDate?: string;
  /** When set, shows a contextual banner indicating the budget was loaded from this historical revision */
  loadedFromRevision?: BudgetRevision;
  /** When true, revision category is required before saving */
  requireRevisionNotes?: boolean;
  /** Used to derive hasResourceBudget for the labour lock */
  resourceBudget?: ResourceBudget;
  onSave: (updatedBudget: Partial<BudgetData>, revisionData?: Partial<BudgetRevision>) => Promise<void>;
  onCancel: () => void;
  /** When true, shows a distinct banner indicating the user is creating a new revision */
  isNewRevision?: boolean;
  /** Called when PM clicks "Make Active" — promotes a draft to the active revision */
  onMakeActive?: () => void;
  /** The designated cascade target category from Budget Settings */
  cascadeTargetCategoryId?: number | null;
  /** Cost Centre mode props */
  isCostCentreMode?: boolean;
  costCentres?: CostCentre[];
  hierarchicalCostCentres?: HierarchicalCostCentre[];
  isStructureView?: boolean;
  projectId?: string;
}

export function EditCostBudgetPage({
  budgetData,
  projectStartDate,
  projectEndDate,
  loadedFromRevision,
  isNewRevision = false,
  requireRevisionNotes = false,
  resourceBudget,
  onSave,
  onCancel,
  onMakeActive,
  cascadeTargetCategoryId,
  isCostCentreMode = false,
  costCentres = [],
  hierarchicalCostCentres = [],
  isStructureView = false,
  projectId,
}: EditCostBudgetPageProps) {
  const currentYear = new Date().getFullYear();

  // Derive hasResourceBudget from the passed resourceBudget prop (or fall back to budgetData)
  const hasResourceBudget =
    (resourceBudget?.byRole?.length ?? budgetData.resourceBudget?.byRole?.length ?? 0) > 0;

  // Calculate years based on project dates
  const years = (() => {
    if (!projectStartDate || !projectEndDate) {
      return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    }
    const startYear = new Date(projectStartDate).getFullYear();
    const endYear = new Date(projectEndDate).getFullYear();
    const yearRange: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      yearRange.push(year);
    }
    return yearRange;
  })();

  const [selectedYear, setSelectedYear] = useState<string>(
    years[0]?.toString() || currentYear.toString()
  );
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOptionalNotes, setShowOptionalNotes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [costBudgetChanges, setCostBudgetChanges] = useState<CostBudget | null>(null);
  const [currentBudgetData, setCurrentBudgetData] = useState<BudgetData>(budgetData);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingSaveAsDraft, setPendingSaveAsDraft] = useState(false);

  // Revision panel state
  const [reasonCategory, setReasonCategory] = useState<RevisionReasonCategory | ''>('');
  const [revisionReason, setRevisionReason] = useState('');

  // Undo/Redo state
  const MAX_HISTORY = 30;
  const [history, setHistory] = useState<CostBudget[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [undoRedoVersion, setUndoRedoVersion] = useState(0);
  const isUndoRedoInProgress = useRef(false);

  // Clean up any stale Radix Dialog body styles on unmount.
  // When this full-screen edit page closes, Radix may leave `pointer-events: none`
  // or `data-scroll-locked` on the body, which prevents the revision history
  // Sheet (and other UI) from functioning.
  useEffect(() => {
    return () => {
      document.body.style.pointerEvents = '';
      document.body.removeAttribute('data-scroll-locked');
    };
  }, []);

  // Handle year change
  const handleYearChange = (year: string) => {
    setSelectedYear(year);

    const yearNum = parseInt(year);
    const currentYearNum = new Date().getFullYear();

    const updatedBudgetData: BudgetData = {
      ...budgetData,
      costBudget: {
        ...budgetData.costBudget,
        categories: budgetData.costBudget.categories.map(cat => {
          if (yearNum === currentYearNum) return cat;
          return {
            ...cat,
            budget: 0,
            actual: 0,
            committed: 0,
            available: 0,
            progressPercent: 0,
          };
        }),
      },
    };

    setCurrentBudgetData(updatedBudgetData);
    setIsDirty(false);
    setCostBudgetChanges(null);
    setHasUserInteracted(false);
    setHistory([]);
    setHistoryIndex(-1);

    console.log(`[EditCostBudgetPage] Year changed to: ${year}`);
  };

  // Save handler — used for both normal Save and Save as Draft
  const handleSave = (asDraft = false) => {
    if (!isDirty) return;

    if (requireRevisionNotes && !reasonCategory) {
      setError('Please select a revision category before saving.');
      return;
    }

    setError(null);
    setPendingSaveAsDraft(asDraft);
    performSave(asDraft);
  };

  const performSave = async (asDraft: boolean) => {
    try {
      setIsSaving(true);
      setError(null);

      // Flush any pending debounced CC budget API writes so the data is
      // persisted before the view re-fetches after save.
      if (isCostCentreMode) {
        await flushPendingCcCostBudgetSaves();
      }

      const updatedBudget: Partial<BudgetData> = {};
      if (costBudgetChanges) {
        updatedBudget.costBudget = costBudgetChanges;
      }

      const revisionData: Partial<BudgetRevision> = {
        reasonCategory: reasonCategory as RevisionReasonCategory,
        reason: revisionReason,
        status: asDraft ? 'draft' : 'active',
        draftType: null,
      };

      await onSave(updatedBudget, revisionData);

      toast.success(asDraft ? 'Draft saved' : 'Cost budget revision saved', {
        description: asDraft
          ? 'This revision is saved as a draft and is not yet active.'
          : reasonCategory
          ? `Saved with category: "${reasonCategory}"`
          : undefined,
      });

      setIsDirty(false);
      setCostBudgetChanges(null);
      setReasonCategory('');
      setRevisionReason('');
      setPendingSaveAsDraft(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save budget');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      setShowUnsavedChangesDialog(true);
    } else {
      onCancel();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleConfirmLeave = () => {
    setShowUnsavedChangesDialog(false);
    onCancel();
  };

  // Undo handler
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoInProgress.current = true;
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      setHistoryIndex(newIndex);
      setCostBudgetChanges(previousState);
      setCurrentBudgetData(prev => ({ ...prev, costBudget: previousState }));
      setIsDirty(newIndex > 0);
      setUndoRedoVersion(v => v + 1);
      setTimeout(() => { isUndoRedoInProgress.current = false; }, 100);
    }
  }, [historyIndex, history]);

  // Redo handler
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoInProgress.current = true;
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      setHistoryIndex(newIndex);
      setCostBudgetChanges(nextState);
      setCurrentBudgetData(prev => ({ ...prev, costBudget: nextState }));
      setIsDirty(true);
      setUndoRedoVersion(v => v + 1);
      setTimeout(() => { isUndoRedoInProgress.current = false; }, 100);
    }
  }, [historyIndex, history]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (
        (e.ctrlKey || e.metaKey) &&
        ((e.shiftKey && e.key === 'z') || e.key === 'y')
      ) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Fetch default GlobalCategories to auto-populate when no cost budget categories exist
  const [defaultGlobalCategories, setDefaultGlobalCategories] = useState<CostCategory[]>([]);

  useEffect(() => {
    if (currentBudgetData.costBudget.categories.length === 0) {
      fetchDefaultCategoriesAsCostCategories()
        .then(categories => {
          console.log('[EditCostBudgetPage] Loaded default GlobalCategories:', categories.length);

          // Pre-populate Labour category budget from resource budget cascade values
          const roles = resourceBudget?.byRole ?? budgetData.resourceBudget?.byRole ?? [];
          if (roles.length > 0) {
            const labourTotal = roles.reduce((sum, role) => sum + (role.budgetCost ?? 0), 0);
            if (labourTotal > 0) {
              categories = categories.map(cat =>
                cat.type === 'labour'
                  ? { ...cat, budget: labourTotal, available: labourTotal }
                  : cat
              );
            }
          }

          setDefaultGlobalCategories(categories);
        })
        .catch(err => {
          console.warn('[EditCostBudgetPage] Failed to load default GlobalCategories:', err);
        });
    }
  }, [currentBudgetData.costBudget.categories.length]);

  const availableCategories =
    currentBudgetData.costBudget.categories.length > 0
      ? currentBudgetData.costBudget.categories
      : defaultGlobalCategories;

  return (
    <div className="min-h-screen bg-background flex flex-col rounded-lg">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                disabled={isSaving}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  {loadedFromRevision ? (
                    <><History className="h-5 w-5 text-blue-500" /> Edit Cost Budget</>
                  ) : isNewRevision ? (
                    <><FilePlus2 className="h-5 w-5 text-emerald-600" /> New Cost Budget Revision</>
                  ) : (
                    <><FileEdit className="h-5 w-5" /> Edit Cost Budget</>
                  )}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Update budget values for {currentBudgetData.projectName}
                </p>
              </div>

              {/* Compact Unsaved Changes Indicator */}
              {isDirty && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-warning-50 border border-warning-200 rounded-md text-warning-700 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Unsaved changes</span>
                </div>
              )}
            </div>

            {/* Import and Export Buttons */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Export feature coming soon')}
                disabled={isSaving}
                title="Export budget"
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info('Import feature coming soon')}
                disabled={isSaving}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
            </div>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mt-3">
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            </div>
          )}
        </div>
      </div>

      {/* Loaded from historical revision banner */}
      {loadedFromRevision && (
        <div className="bg-blue-50 border-b border-blue-200 px-4 py-2.5 flex items-center gap-2 text-blue-800 text-sm">
          <Info className="h-4 w-4 flex-shrink-0 text-blue-500" />
          <span>
            Loaded from{' '}
            <strong>CB-v{loadedFromRevision.revisionNumber}</strong>
            {loadedFromRevision.date
              ? ` — ${new Date(loadedFromRevision.date).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}`
              : ''}
            . You are editing a copy of this historical revision. Saving will create a new draft.
          </span>
        </div>
      )}

      {/* New Revision banner */}
      {isNewRevision && !loadedFromRevision && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-2.5 flex items-center gap-2 text-emerald-800 text-sm">
          <FilePlus2 className="h-4 w-4 flex-shrink-0 text-emerald-600" />
          <span>
            <strong>Creating New Revision</strong> — You are working on a new cost budget revision. Your changes will be saved as a new draft revision.
          </span>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 pb-4 space-y-6">
          <EditCostBudgetForm
            key={`${selectedYear}-${undoRedoVersion}`}
            costBudget={currentBudgetData.costBudget}
            selectedYear={selectedYear}
            projectStartDate={projectStartDate}
            projectEndDate={projectEndDate}
            availableCategories={availableCategories}
            resourceBudget={currentBudgetData.resourceBudget}
            hasResourceBudget={hasResourceBudget}
            cascadeTargetCategoryId={cascadeTargetCategoryId}
            isCostCentreMode={isCostCentreMode}
            costCentres={costCentres}
            hierarchicalCostCentres={hierarchicalCostCentres}
            isStructureView={isStructureView}
            projectId={projectId}
            onChange={(changes) => {
              setCostBudgetChanges(changes);

              if (isUndoRedoInProgress.current) return;

              if (!hasUserInteracted) {
                setHasUserInteracted(true);
                setHistory([currentBudgetData.costBudget, changes]);
                setHistoryIndex(1);
                setIsDirty(true);
                return;
              }

              setIsDirty(true);

              const newHistory = history.slice(0, historyIndex + 1);
              newHistory.push(changes);

              if (newHistory.length > MAX_HISTORY) {
                newHistory.shift();
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              } else {
                setHistory(newHistory);
                setHistoryIndex(newHistory.length - 1);
              }
            }}
          />

          {/* Revision panel — required when requireRevisionNotes is on, optional otherwise */}
          {isDirty && requireRevisionNotes && (
            <BudgetRevisionPanel
              changePercentage={0}
              requiresApproval={false}
              reasonCategory={reasonCategory}
              onReasonCategoryChange={setReasonCategory}
              revisionReason={revisionReason}
              onRevisionReasonChange={setRevisionReason}
              costBudgetChanges={costBudgetChanges}
              salesBudgetChanges={null}
              resourceBudgetChanges={null}
              originalBudget={budgetData}
            />
          )}

          {/* Optional notes when requireRevisionNotes is off */}
          {isDirty && !requireRevisionNotes && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 w-full text-left"
                onClick={() => setShowOptionalNotes(prev => !prev)}
              >
                <MessageSquare className="h-4 w-4" />
                {showOptionalNotes ? 'Hide notes' : 'Add notes (optional)'}
              </button>
              {showOptionalNotes && (
                <div className="mt-3 space-y-3">
                  <BudgetRevisionPanel
                    changePercentage={0}
                    requiresApproval={false}
                    reasonCategory={reasonCategory}
                    onReasonCategoryChange={setReasonCategory}
                    revisionReason={revisionReason}
                    onRevisionReasonChange={setRevisionReason}
                    costBudgetChanges={costBudgetChanges}
                    salesBudgetChanges={null}
                    resourceBudgetChanges={null}
                    originalBudget={budgetData}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t shadow-lg">
        <div className="px-4 py-3">
          <div className="flex justify-end items-center gap-3">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={isSaving}
              size="lg"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>

            <Button
              variant="outline"
              onClick={handleUndo}
              disabled={historyIndex <= 0 || isSaving}
              size="lg"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="h-4 w-4 mr-2" />
              Undo {historyIndex > 0 && `(${historyIndex})`}
            </Button>

            <Button
              variant="outline"
              onClick={handleRedo}
              disabled={historyIndex >= history.length - 1 || isSaving}
              size="lg"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo className="h-4 w-4 mr-2" />
              Redo {historyIndex < history.length - 1 && `(${history.length - 1 - historyIndex})`}
            </Button>

            {loadedFromRevision || onMakeActive ? (
              <>
                {/* Draft / historical revision mode — Save as Draft + Make Active */}
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  disabled={!isDirty || isSaving}
                  size="lg"
                >
                  <FileEdit className="h-4 w-4 mr-2" />
                  {isSaving && pendingSaveAsDraft ? 'Saving…' : 'Save as Draft'}
                </Button>

                <Button
                  onClick={onMakeActive}
                  disabled={!isDirty || isSaving}
                  size="lg"
                  className="bg-success-600 hover:bg-success-700 text-white"
                >
                  Make Active
                </Button>
              </>
            ) : (
              /* Normal edit of active revision — single Save button */
              <Button
                onClick={() => handleSave(false)}
                disabled={!isDirty || isSaving}
                size="lg"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving && !pendingSaveAsDraft ? 'Saving…' : 'Save Budget'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedChangesDialog} onOpenChange={setShowUnsavedChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure that you want to leave the current page? The changes that you made won't be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave}>Leave</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
